// characters: шаг стартового меню «Персонаж» — карусель из трёх персонажей
// (крутящееся превью-спрайт), характеристики, апгрейды за серебро.
// Показывается один раз после входа (до «Начать») и по кнопке «ПЕРСОНАЖ»
// в главном меню. Разметка и стили создаются здесь — index.html не трогаем.

import {
  CHARACTERS, STAT_KEYS, STAT_LABELS, characterById, type CharacterDef, type StatKey,
} from "./registry";
import {
  buyUpgrade, effectLabel, effectiveLevels, fetchProgress, saveCharacter, upgradePrice,
  DEFAULT_RULES, type PlayerProgress,
} from "./progress";
import { getSelectedCharId, setSelectedCharId } from "./state";
import { LANG_EVENT, t } from "../i18n";

export interface CharacterMenuDeps {
  /** showCard(null) из main.ts — спрятать остальные карточки меню. */
  hideOtherCards: () => void;
  /** Вернуться в главное меню (showCard("menu")). */
  showMainMenu: () => void;
  /** Идёт ли матч (смена персонажа применится со следующего входа). */
  inGame: () => boolean;
}

const PREVIEW_W = 160;
const PREVIEW_H = 240;
const PREVIEW_SCALE = 1.4;
const TURN_FPS = 8;

const CSS = `
#overlay.charStep #authGuestCard, #overlay.charStep #authProfileCard,
#overlay.charStep #pauseResumeRow, #overlay.charStep > .hint { display: none !important; }
.charCard { width: 680px; max-width: 96vw; gap: 10px; }
.charCard__body { display: grid; grid-template-columns: auto 1fr; gap: 18px; align-items: center; }
.charCard__left { display: flex; flex-direction: column; align-items: center; gap: 8px; }
.charCard__right { display: flex; flex-direction: column; gap: 8px; min-width: 0; }
.charCard__stage { display: flex; align-items: center; justify-content: center; gap: 6px; }
.charCard__arrow {
  font: inherit; font-size: 34px; line-height: 1; width: 40px; height: 64px; padding: 0;
  background: #2a2a30; color: #ccc; border: 1px solid #3a3a42; border-radius: 6px; cursor: pointer;
}
.charCard__arrow:hover { background: #35353d; color: #fff; }
.charCard__preview {
  width: ${PREVIEW_W * PREVIEW_SCALE}px; height: ${PREVIEW_H * PREVIEW_SCALE}px;
  background-repeat: no-repeat; image-rendering: pixelated; cursor: grab; touch-action: pan-y;
  background-color: #0c0b10; border: 1px solid #26242c; border-radius: 6px;
  box-shadow: inset 0 -40px 60px rgba(120, 20, 20, 0.18);
}
.charCard__preview:active { cursor: grabbing; }
.charCard__dots { display: flex; justify-content: center; gap: 8px; }
.charCard__dot { width: 9px; height: 9px; border-radius: 50%; background: #33313a; border: none; padding: 0; cursor: pointer; }
.charCard__dot.on { background: #c33; }
.charCard__name { font-size: 34px; letter-spacing: 3px; color: #eee; line-height: 1; }
.charCard__role { font-size: 16px; color: #b9a27a; letter-spacing: 1px; }
.charCard__blurb { font-size: 16px; color: #999; min-height: 2.4em; }
.charCard__stats { display: flex; flex-direction: column; gap: 7px; margin-top: 4px; }
.charStat { display: grid; grid-template-columns: 84px 1fr 92px 64px; align-items: center; gap: 8px; }
.charStat__label { font-size: 16px; color: #ccc; letter-spacing: 1px; }
.charStat__label.bonus { color: #e3b75a; }
.charStat__pips { display: flex; gap: 3px; }
.charStat__pip { flex: 1; height: 11px; background: #26242c; border-radius: 2px; }
.charStat__pip.bought { background: #c33; }
.charStat__pip.bonus { background: #e3b75a; }
.charStat__pip.locked { background: repeating-linear-gradient(45deg, #26242c 0 3px, #1a1920 3px 6px); }
.charStat__effect { font-size: 15px; color: #aaa; text-align: right; white-space: nowrap; }
.charStat__buy {
  font: inherit; font-size: 15px; padding: 3px 6px; border-radius: 5px; cursor: pointer;
  background: #2a2a30; color: #ddd; border: 1px solid #3a3a42; white-space: nowrap;
}
.charStat__buy:hover:not(:disabled) { background: #3a3530; color: #fff; border-color: #6a5a3a; }
.charStat__buy:disabled { opacity: 0.45; cursor: not-allowed; }
.charCard__wallet { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; font-size: 15px; color: #888; flex-wrap: wrap; }
.charCard__coins { color: #c9c9d6; font-size: 20px; }
.charCard__coins b { color: #f0f0ff; font-weight: normal; }
.charCard__note { font-size: 14px; color: #777; }
.charCard__note.warn { color: #d99; }
#menuCharacter .menuBtn__label b { color: #e3b75a; font-weight: inherit; }
@media (max-width: 700px) {
  .charCard__body { grid-template-columns: 1fr; gap: 8px; }
  .charCard__right { align-items: stretch; text-align: center; }
  .charCard__preview { width: ${PREVIEW_W}px; height: ${PREVIEW_H}px; }
  .charStat { grid-template-columns: 86px 1fr 66px 54px; gap: 6px; text-align: left; }
}
@media (max-height: 560px) {
  .charCard__preview { width: ${PREVIEW_W}px; height: ${PREVIEW_H}px; }
  .charCard__blurb { display: none; }
}
`;

let deps: CharacterMenuDeps | null = null;
let card: HTMLDivElement | null = null;
let preview: HTMLDivElement | null = null;
let menuBtn: HTMLButtonElement | null = null;
let progress: PlayerProgress | null = null;
let shownOnce = false;
let frame = 0;
let turnTimer: number | null = null;
let dragX: number | null = null;
let dragFrame0 = 0;
/** Идёт покупка — кнопки «＋» неактивны до ответа (FullProof). */
let buying = false;

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

function current(): CharacterDef {
  return characterById(getSelectedCharId());
}

function q<T extends HTMLElement>(sel: string): T {
  return card!.querySelector(sel) as T;
}

function setStatus(msg: string, warn = false): void {
  const s = q<HTMLDivElement>(".dialogCard__status");
  s.textContent = msg;
  s.classList.toggle("warn", warn);
  s.classList.toggle("ok", !warn && msg !== "");
}

function drawFrame(): void {
  if (!preview) return;
  const def = current();
  const n = def.turntableFrames;
  const f = ((frame % n) + n) % n;
  const w = preview.clientWidth || PREVIEW_W * PREVIEW_SCALE;
  const h = preview.clientHeight || PREVIEW_H * PREVIEW_SCALE;
  preview.style.backgroundImage = `url("${def.turntable}")`;
  preview.style.backgroundSize = `${w * n}px ${h}px`;
  preview.style.backgroundPosition = `${-f * w}px 0px`;
}

function startTurn(): void {
  stopTurn();
  turnTimer = window.setInterval(() => {
    if (dragX === null) {
      frame++;
      drawFrame();
    }
  }, 1000 / TURN_FPS);
}

function stopTurn(): void {
  if (turnTimer !== null) {
    clearInterval(turnTimer);
    turnTimer = null;
  }
}

function render(): void {
  if (!card) return;
  const def = current();
  q<HTMLDivElement>(".charCard__name").textContent = t(def.name);
  q<HTMLDivElement>(".charCard__role").textContent = t(def.title);
  q<HTMLDivElement>(".charCard__blurb").textContent = t(def.blurb);
  card.querySelectorAll<HTMLButtonElement>(".charCard__dot").forEach((d, i) => {
    d.classList.toggle("on", i === def.id);
  });
  drawFrame();

  const p = progress;
  const rules = p?.rules ?? DEFAULT_RULES;
  const bought = p?.levels ?? { hp: 0, speed: 0, armor: 0 };
  const guest = p?.guest ?? true;
  const eff = effectiveLevels(def.id, bought, guest, rules);
  const cap = guest ? rules.guestMaxLevel : rules.maxLevel;
  for (const k of STAT_KEYS) {
    const row = q<HTMLDivElement>(`.charStat[data-stat="${k}"]`);
    const isBonus = def.bonus === k;
    const lbl = row.querySelector(".charStat__label") as HTMLDivElement;
    lbl.textContent = t(STAT_LABELS[k]);
    lbl.classList.toggle("bonus", isBonus);
    lbl.title = isBonus ? t("Сильная сторона персонажа: +1 уровень бесплатно") : "";
    const pips = row.querySelectorAll<HTMLDivElement>(".charStat__pip");
    const boughtEff = Math.min(cap, bought[k]);
    pips.forEach((pip, i) => {
      const lvl = i + 1;
      pip.className = "charStat__pip";
      if (lvl <= boughtEff) pip.classList.add("bought");
      else if (lvl <= eff[k]) pip.classList.add("bonus");
      else if (lvl > cap) pip.classList.add("locked");
    });
    (row.querySelector(".charStat__effect") as HTMLDivElement).textContent = effectLabel(k, eff[k], rules);
    const btn = row.querySelector(".charStat__buy") as HTMLButtonElement;
    const next = bought[k] + 1;
    const price = upgradePrice(next, rules);
    if (bought[k] >= rules.maxLevel) {
      btn.textContent = t("макс.");
      btn.disabled = true;
      btn.title = "";
    } else if (bought[k] >= cap) {
      btn.textContent = "🔒";
      btn.disabled = false; // по клику — объяснение (войти в аккаунт)
      btn.title = t("Гостю — до {cap} уровня", { cap });
    } else {
      btn.textContent = `+ ${price}`;
      btn.disabled = buying || !p || p.coins < price;
      btn.title = t("Уровень {next}: {price} серебра", { next, price });
    }
  }
  q<HTMLElement>(".charCard__coins b").textContent = String(p?.coins ?? 0);
  const e = rules.earn;
  q<HTMLDivElement>(".charCard__earn").textContent =
    t("фраг {kill} · бот {bot} · раунд {round} · матч {match}",
      { kill: e.playerKill, bot: e.npcKill, round: e.roundWin, match: e.matchFinished }) +
    t(" · реликвии\u00a0до\u00a0{cap}", { cap: e.relicSessionCap }); // artifacts: фраза не рвётся на телефоне
  const note = q<HTMLDivElement>(".charCard__note");
  if (deps?.inGame()) {
    note.textContent = t("Новый персонаж и уровни — со следующего входа в матч.");
    note.classList.add("warn");
  } else if (guest) {
    note.textContent = t("Гость: прогресс хранится в этом браузере, уровни до {guestMax}. Войдите, чтобы сохранять серебро и качать до {max}.",
      { guestMax: rules.guestMaxLevel, max: rules.maxLevel });
    note.classList.remove("warn");
  } else {
    note.textContent = t("Уровни общие для всех персонажей. Сильная сторона персонажа — золотом.");
    note.classList.remove("warn");
  }
  updateMenuButton();
}

function updateMenuButton(): void {
  if (!menuBtn) return;
  const lbl = menuBtn.querySelector(".menuBtn__label") as HTMLSpanElement;
  lbl.innerHTML = "";
  lbl.append(t("ПЕРСОНАЖ:") + " ", el("b", undefined, t(current().name).toUpperCase()));
  const sub = menuBtn.querySelector(".menuBtn__sub") as HTMLSpanElement;
  sub.textContent = progress ? t("серебро {n} · апгрейды", { n: progress.coins }) : t("выбор и апгрейды");
}

function select(id: number): void {
  const n = CHARACTERS.length;
  const nid = ((id % n) + n) % n;
  if (nid === getSelectedCharId()) return;
  setSelectedCharId(nid);
  frame = 0;
  setStatus("");
  if (progress) void saveCharacter(progress, nid);
  render();
}

async function onBuy(stat: StatKey): Promise<void> {
  if (!progress || buying) return;
  const rules = progress.rules;
  const cap = progress.guest ? rules.guestMaxLevel : rules.maxLevel;
  if (progress.levels[stat] >= cap && progress.levels[stat] < rules.maxLevel) {
    setStatus(t("Гостю доступно до {cap} уровня — войдите, чтобы качать дальше.", { cap }), true);
    return;
  }
  buying = true;
  render();
  try {
    const r = await buyUpgrade(progress, stat);
    if (r.ok) {
      progress = r.progress;
      setStatus(t("{stat}: уровень {n}.", { stat: t(STAT_LABELS[stat]), n: progress.levels[stat] }));
    } else {
      setStatus(r.error, true);
    }
  } finally {
    buying = false;
    render();
  }
}

function build(): void {
  const style = el("style");
  style.textContent = CSS;
  document.head.append(style);

  card = el("div", "dialogCard charCard hidden");
  card.id = "charCard";
  card.dataset.i18nJs = "";
  card.setAttribute("aria-label", t("Выбор персонажа"));
  card.append(el("div", "dialogCard__title", t("Персонаж")));

  const stage = el("div", "charCard__stage");
  const prev = el("button", "charCard__arrow", "‹");
  prev.type = "button";
  prev.setAttribute("aria-label", t("Предыдущий персонаж"));
  prev.addEventListener("click", () => select(getSelectedCharId() - 1));
  const next = el("button", "charCard__arrow", "›");
  next.type = "button";
  next.setAttribute("aria-label", t("Следующий персонаж"));
  next.addEventListener("click", () => select(getSelectedCharId() + 1));
  preview = el("div", "charCard__preview");
  preview.title = t("Потяните, чтобы повернуть");
  preview.addEventListener("pointerdown", (e) => {
    dragX = e.clientX;
    dragFrame0 = frame;
    preview!.setPointerCapture(e.pointerId);
  });
  preview.addEventListener("pointermove", (e) => {
    if (dragX === null) return;
    frame = dragFrame0 - Math.round((e.clientX - dragX) / 14);
    drawFrame();
  });
  const endDrag = (e: PointerEvent): void => {
    if (dragX === null) return;
    const dx = e.clientX - dragX;
    dragX = null;
    // Короткий свайп по превью на телефоне = листать персонажей.
    if (e.pointerType === "touch" && Math.abs(dx) > 70 && Math.abs(frame - dragFrame0) > 4) {
      select(getSelectedCharId() + (dx < 0 ? 1 : -1));
    }
  };
  preview.addEventListener("pointerup", endDrag);
  preview.addEventListener("pointercancel", endDrag);
  stage.append(prev, preview, next);
  const body = el("div", "charCard__body");
  const left = el("div", "charCard__left");
  const right = el("div", "charCard__right");
  left.append(stage);
  body.append(left, right);
  card.append(body);

  const dots = el("div", "charCard__dots");
  for (const c of CHARACTERS) {
    const d = el("button", "charCard__dot");
    d.type = "button";
    d.setAttribute("aria-label", t(c.name));
    d.addEventListener("click", () => select(c.id));
    dots.append(d);
  }
  left.append(dots);
  right.append(el("div", "charCard__name"), el("div", "charCard__role"), el("div", "charCard__blurb"));

  const stats = el("div", "charCard__stats");
  for (const k of STAT_KEYS) {
    const row = el("div", "charStat");
    row.dataset.stat = k;
    const pips = el("div", "charStat__pips");
    for (let i = 0; i < DEFAULT_RULES.maxLevel; i++) pips.append(el("div", "charStat__pip"));
    const buy = el("button", "charStat__buy");
    buy.type = "button";
    buy.addEventListener("click", () => void onBuy(k));
    row.append(el("div", "charStat__label"), pips, el("div", "charStat__effect"), buy);
    stats.append(row);
  }
  right.append(stats);

  const wallet = el("div", "charCard__wallet");
  const coins = el("div", "charCard__coins", t("Серебро:") + " ");
  coins.append(el("b", undefined, "0"));
  wallet.append(coins, el("div", "charCard__earn"));
  card.append(wallet, el("div", "charCard__note"), el("div", "dialogCard__status"));

  const buttons = el("div", "dialogCard__buttons");
  const go = el("button", "primary", t("Готово →"));
  go.type = "button";
  go.addEventListener("click", () => hideCharacterStep(true));
  buttons.append(go);
  card.append(buttons);

  const overlay = document.getElementById("overlay");
  const loader = document.getElementById("loader");
  if (overlay) overlay.insertBefore(card, loader && loader.parentElement === overlay ? loader : null);

  // Кнопка в главном меню — первой, над SINGLE PLAYER.
  const mainMenu = document.getElementById("mainMenu");
  if (mainMenu) {
    menuBtn = el("button", "menuBtn secondary");
    menuBtn.type = "button";
    menuBtn.id = "menuCharacter";
    menuBtn.dataset.i18nJs = "";
    menuBtn.append(el("span", "menuBtn__label"), el("span", "menuBtn__sub"));
    menuBtn.addEventListener("click", () => showCharacterStep());
    mainMenu.insertBefore(menuBtn, mainMenu.firstChild);
    updateMenuButton();
  }

  window.addEventListener("keydown", (e) => {
    if (!card || card.classList.contains("hidden")) return;
    if (e.key === "ArrowLeft") { select(getSelectedCharId() - 1); e.preventDefault(); }
    else if (e.key === "ArrowRight") { select(getSelectedCharId() + 1); e.preventDefault(); }
    else if (e.key === "Enter") { hideCharacterStep(true); e.preventDefault(); }
  });
}

/** Один раз из main.ts (после того как есть showCard). */
export function initCharacterMenu(d: CharacterMenuDeps): void {
  deps = d;
  if (!card) build();
  window.addEventListener(LANG_EVENT, () => render());
  void refreshCharacterProgress();
}

/** Перечитать прогресс (после входа/выхода, после матча). */
export async function refreshCharacterProgress(): Promise<void> {
  progress = await fetchProgress();
  // Аккаунт помнит персонажа на сервере (между устройствами) — берём его,
  // если в этой вкладке не задан явный ?char=.
  if (!progress.guest && !new URLSearchParams(location.search).has("char")) {
    setSelectedCharId(progress.charId);
  }
  render();
}

/** Показать шаг «Персонаж». */
export function showCharacterStep(): void {
  if (!card || !deps) return;
  deps.hideOtherCards();
  card.classList.remove("hidden");
  document.getElementById("overlay")?.classList.add("charStep");
  setStatus("");
  render();
  startTurn();
  void refreshCharacterProgress();
}

/** Спрятать шаг; toMenu — вернуться в главное меню. */
export function hideCharacterStep(toMenu = false): void {
  if (!card || card.classList.contains("hidden")) return;
  card.classList.add("hidden");
  document.getElementById("overlay")?.classList.remove("charStep");
  stopTurn();
  if (toMenu) deps?.showMainMenu();
}

/**
 * Вызывается после применения авторизации к меню. Стартовый поток: если
 * главное меню видно — один раз за загрузку страницы показываем шаг
 * «Персонаж» (до «Начать»); иначе просто обновляем прогресс (вход/выход).
 */
export function onAuthAppliedForCharacters(menuVisible: boolean): void {
  if (!deps) return;
  if (menuVisible && !shownOnce && !deps.inGame()) {
    shownOnce = true;
    showCharacterStep();
    return;
  }
  void refreshCharacterProgress();
}
